import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { previewInvite, acceptInvite, declineInvite } from './services/orgInviteApi';

const pageStyle = {
  minHeight: '60vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
};

const cardStyle = {
  width: '100%',
  maxWidth: 420,
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  padding: '32px 28px',
  textAlign: 'center',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  margin: '8px 0 16px',
  border: '1px solid #ccc',
  borderRadius: 4,
  boxSizing: 'border-box',
};

const buttonStyle = {
  width: '100%',
  padding: '12px',
  border: 'none',
  borderRadius: 4,
  background: '#4b2ee0',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: 'transparent',
  color: '#666',
  border: '1px solid #ccc',
  marginTop: 12,
};

const labelStyle = { display: 'block', textAlign: 'left', fontSize: 13, color: '#666' };

/**
 * Public invite-acceptance page. Deliberately session-agnostic: it never reads
 * or writes the logged-in user's session (userId/token in localStorage), so
 * opening an invite link in a browser where someone is already signed in does
 * not disturb that session. Acceptance is authorized purely by the invite token.
 */
function AcceptInvite() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // 'choose' → Accept / Decline; 'password' → set-password form for a new account.
  const [stage, setStage] = useState('choose');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      setLoading(true);
      setInvalid(false);
      try {
        const result = await previewInvite(token);
        if (cancelled) return;
        if (!result || !result.ok) {
          setInvalid(true);
        } else {
          setInvite(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setInvalid(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitAccept = async (payload) => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await acceptInvite(payload);
      if (result && result.ok) {
        setAccepted(true);
      } else {
        setSubmitError(
          (result && result.data && result.data.message) ||
            'Could not accept the invite — it may have expired. Ask your admin to resend.',
        );
      }
    } catch (err) {
      setSubmitError('Could not accept the invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Accept clicked. A brand-new email (no account) is routed to the set-password
  // step; an existing account is accepted by token — the backend links the
  // account whose email matches this invite, so no login is required.
  const handleAcceptClick = async () => {
    setSubmitError('');
    if (invite && invite.accountExists === false) {
      setStage('password');
      return;
    }
    await submitAccept({ token });
  };

  const handleCreateAccountSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!password || password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    await submitAccept({ token, password });
  };

  const handleDecline = async () => {
    try {
      await declineInvite({ token });
    } catch (err) {
      // Best-effort — still show the declined state even if the request fails.
    }
    setDeclined(true);
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2>Loading invite…</h2>
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2>Invite declined</h2>
          <p>You can close this page.</p>
        </div>
      </div>
    );
  }

  if (invalid || !invite) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2>Invite not found</h2>
          <p>This invite is no longer valid — ask your admin to resend.</p>
        </div>
      </div>
    );
  }

  if (accepted) {
    const isNewAccount = invite.accountExists === false;
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2>You&apos;re in!</h2>
          <p>
            You&apos;ve joined <strong>{invite.orgName || 'the organization'}</strong> as{' '}
            {invite.role || 'instructor'}.
          </p>
          <p style={{ color: '#666', fontSize: 14 }}>
            {isNewAccount ? (
              <>
                Your account is ready — log in with <strong>{invite.email}</strong> and your new
                password.
              </>
            ) : (
              <>Log in to switch into {invite.orgName || 'the organization'}.</>
            )}
          </p>
          <Link to="/login">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2>Join {invite.orgName || 'your organization'}</h2>
        <p>
          You&apos;ve been invited as <strong>{invite.role}</strong>.
        </p>
        <p style={{ color: '#666', fontSize: 14 }}>{invite.email}</p>

        {stage === 'password' ? (
          <form onSubmit={handleCreateAccountSubmit}>
            <p style={{ textAlign: 'left', fontSize: 13, color: '#666', marginBottom: 12 }}>
              Set a password to create your account and join.
            </p>

            <label htmlFor="accept-invite-password" style={labelStyle}>
              Password
            </label>
            <input
              id="accept-invite-password"
              type="password"
              style={inputStyle}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />

            <label htmlFor="accept-invite-confirm-password" style={labelStyle}>
              Confirm password
            </label>
            <input
              id="accept-invite-confirm-password"
              type="password"
              style={inputStyle}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />

            {submitError && <p style={{ color: '#c62828' }}>{submitError}</p>}

            <button type="submit" style={buttonStyle} disabled={submitting}>
              {submitting ? 'Creating account…' : 'Create account & join'}
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={() => {
                setStage('choose');
                setSubmitError('');
              }}
              disabled={submitting}
            >
              Back
            </button>
          </form>
        ) : (
          <>
            {submitError && <p style={{ color: '#c62828' }}>{submitError}</p>}
            <button type="button" style={buttonStyle} onClick={handleAcceptClick} disabled={submitting}>
              {submitting ? 'Accepting…' : 'Accept'}
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={handleDecline}
              disabled={submitting}
            >
              Decline
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;
